import { isSupportedLanguage, LANGUAGE_CONFIG } from "../../config";

/**
 * Strips a trailing slash from a pathname, leaving `/` unchanged.
 *
 * @param {string} pathname Path to normalize.
 * @returns {string} Path without a trailing slash.
 */
const stripTrailingSlash = (pathname) => {
  if (!pathname || pathname === "/") {
    return pathname || "/";
  }
  return pathname.replace(/\/$/, "");
};

/**
 * Returns the backend origin used by public auth forms.
 *
 * @returns {string} Backend origin without a trailing slash.
 */
export const getAuthBackendOrigin = () => {
  if (window.location.port === "3000") {
    return `${window.location.protocol}//${window.location.hostname}:5000`;
  }

  return window.location.origin;
};

/**
 * Returns a React-app next URL suitable for backend redirects after login.
 *
 * @param {string|null} nextParam Next value from the login page query string.
 * @returns {string} Redirect target for successful login.
 */
export const getLoginNextUrl = (nextParam) => {
  if (nextParam?.startsWith("http")) {
    const nextUrl = new URL(nextParam);
    if (nextUrl.pathname === "/") {
      return `${nextUrl.origin}/app/home`;
    }
    return nextParam;
  }

  const nextPath = nextParam && nextParam !== "/" ? nextParam : "/app/home";
  return `${window.location.origin}${nextPath}`;
};

/**
 * Supported language code at the end of a login path, or null.
 *
 * @param {string} pathname Path including or excluding `/app`.
 * @returns {string|null} Preferred-language code, or null for bare `/login`.
 */
const languageCodeFromLoginPath = (pathname) => {
  const normalized = stripTrailingSlash(pathname);
  const match = LANGUAGE_CONFIG.OPTIONS.find((option) =>
    normalized.endsWith(`/login/${option.code}`)
  );
  return match?.code || null;
};

/**
 * Resolves login-page language from a pathname (router or window).
 * `/login/<code>` selects that language. Bare `/login` uses the default
 * without persisting.
 *
 * @param {string} [pathname=window.location.pathname] Path including or excluding `/app`.
 * @returns {string} Preferred-language code.
 */
export const getLoginPathLanguage = (pathname = window.location.pathname) =>
  languageCodeFromLoginPath(pathname) || LANGUAGE_CONFIG.DEFAULT;

/**
 * Language to write on successful login, or `null` when the path must not persist.
 *
 * @param {string} [pathname=window.location.pathname] Path including or excluding `/app`.
 * @returns {string|null} Preferred-language code, or `null` for bare `/login`.
 */
export const getPersistableLoginLanguage = (
  pathname = window.location.pathname
) => languageCodeFromLoginPath(pathname);

/**
 * React-router login path for a language, keeping the current query string.
 * Uses `/login/<code>` so a manual toggle also persists.
 *
 * @param {string} language Preferred-language code.
 * @param {string} [search=""] Query string including `?`.
 * @returns {string} Path under the `/app` basename.
 */
export const getLoginRouteForLanguage = (language, search = "") => {
  const code = isSupportedLanguage(language)
    ? language
    : LANGUAGE_CONFIG.DEFAULT;
  return `/login/${code}${search || ""}`;
};

/**
 * Whether a router or window path is a language-force override.
 *
 * @param {string} [pathname=window.location.pathname] Path with or without `/app`.
 * @returns {boolean} True for `/<code>` and `/app/<code>` of a supported language.
 */
export const isLanguageForcePath = (pathname = window.location.pathname) => {
  const normalized = stripTrailingSlash(pathname);
  return LANGUAGE_CONFIG.OPTIONS.some(
    (option) =>
      normalized === `/${option.code}` || normalized === `/app/${option.code}`
  );
};

/**
 * Absolute login URL for a language-force guest redirect.
 *
 * @param {string} language Preferred-language code.
 * @returns {string} Same-origin `/app/login/<code>`.
 */
export const getLanguageForceLoginHref = (language) =>
  `${window.location.origin}/app${getLoginRouteForLanguage(language)}`;

/**
 * Absolute site-home URL after a successful language-force write.
 *
 * @returns {string} Django BASE_URL `/` (localhost uses the :5000 origin).
 */
export const getLanguageForceHomeHref = () => `${getAuthBackendOrigin()}/`;

/**
 * Login route for an unauthenticated visitor, matching language-force suffixes.
 *
 * @param {string} currentPath Full window path plus search (`/app/es`).
 * @returns {string} Basename-relative login path with `next`.
 */
export const getGuestLoginRoute = (currentPath) => {
  const pathname = stripTrailingSlash(currentPath.split("?")[0] || "/");
  const next = encodeURIComponent(currentPath);
  const option = LANGUAGE_CONFIG.OPTIONS.find(
    (item) => pathname === `/app/${item.code}` || pathname === `/${item.code}`
  );
  if (option) {
    return `/login/${option.code}?next=${next}`;
  }
  return `/login?next=${next}`;
};
